import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  BarChart3, TrendingUp, DollarSign, ShoppingCart, Users,
  Filter, RefreshCw, ChevronDown, ChevronUp, Award, Clock,
  CheckCircle, XCircle, AlertTriangle, Percent, ArrowUp, ArrowDown,
  PieChart as PieChartIcon, Activity, Zap, Eye, ThumbsUp, X, RotateCcw,
  UserPlus, UserCheck, UserX, UserMinus, Ban, Calendar, Package, PackageCheck, Archive
} from 'lucide-react';
import { custoAPI, pedidoAPI, clienteAPI } from './services/api';
import Loading from './components/Loading';
import { useAuth } from './contexts/AuthContext';

// ==================== CORES DA MARCA ====================
const CORES = {
  azulEscuro: '#1F3D7A',
  azulMedio: '#0D5DFC',
  ciano: '#00BEFF',
  cinza: '#9C9B9B',
  cinzaClaro: '#F8F9FA',
  verde: '#10B981',
  vermelho: '#EF4444',
  amarelo: '#F59E0B',
  laranja: '#F97316'
};

// ==================== COMPONENTE DASHBOARD ANALÍTICO ====================
const DashboardAnalitico = ({ pedidosFiltrados: pedidosFiltradosPai, listaResponsaveis, formatarMoeda, formatarPorcentagem, CORES }) => {
  const [responsavelSelecionado, setResponsavelSelecionado] = useState('todos');
  const [mostrarFiltrosAnalitico, setMostrarFiltrosAnalitico] = useState(false);
  
  // Estado para ordenação da tabela
  const [ordenacao, setOrdenacao] = useState({ coluna: 'valorTotal', direcao: 'desc' });

  // Função para alternar ordenação
  const alternarOrdenacao = (coluna) => {
    setOrdenacao(prev => ({
      coluna,
      direcao: prev.coluna === coluna && prev.direcao === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Componente do botão de ordenação
  const BotaoOrdenacao = ({ coluna, children }) => (
    <button
      onClick={() => alternarOrdenacao(coluna)}
      className="flex items-center gap-1 hover:text-blue-600 transition-colors duration-200 group"
    >
      {children}
      <span className={`transition-all duration-200 ${ordenacao.coluna === coluna ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}`}>
        {ordenacao.coluna === coluna ? (
          ordenacao.direcao === 'desc' ? (
            <ArrowDown size={14} className="text-blue-600" />
          ) : (
            <ArrowUp size={14} className="text-blue-600" />
          )
        ) : (
          <ArrowDown size={14} />
        )}
      </span>
    </button>
  );

  // Filtrar pedidos apenas pelo representante selecionado (os demais filtros já vêm do Dashboard principal)
  const pedidosFiltrados = pedidosFiltradosPai.filter(p => {
    const dentroResponsavel = responsavelSelecionado === 'todos' || p.vendedor === responsavelSelecionado;
    return dentroResponsavel;
  });

  // Calcular área total (m²) de um pedido
  const calcularAreaPedido = (pedido) => {
    return parseFloat(pedido.total_m2_geral) || 0;
  };

  // Análise por representante
  const analisePorRepresentante = () => {
    const analise = {};
    
    pedidosFiltrados.forEach(p => {
      const vendedor = p.vendedor || 'Não informado';
      if (!analise[vendedor]) {
        analise[vendedor] = {
          nome: vendedor,
          qtdPedidos: 0,
          valorTotal: 0,
          custoTotal: 0,
          lucroTotal: 0,
          metrosQuadrados: 0,
          metrosForros: 0,
          valorForros: 0,
          valorAcessorios: 0,
          pedidos: []
        };
      }
      
      const valorPedido = (parseFloat(p.valor_tabela1) || 0) + (parseFloat(p.valor_tabela2) || 0) + (parseFloat(p.valor_produtos_especiais) || 0);
      const custoPedido = (parseFloat(p.custo_tabela1) || 0) + (parseFloat(p.custo_tabela2) || 0);
      const m2Pedido = parseFloat(p.total_m2_geral) || 0;
      const valorForroPedido = parseFloat(p.valor_forro_geral) || 0;
      const valorAcessoriosPedido = parseFloat(p.valor_acessorios_geral) || 0;
      
      // Calcular m² de forros
      analise[vendedor].metrosForros += m2Pedido;
      
      analise[vendedor].qtdPedidos += 1;
      analise[vendedor].valorTotal += valorPedido;
      analise[vendedor].custoTotal += custoPedido;
      analise[vendedor].lucroTotal += (valorPedido - custoPedido);
      analise[vendedor].metrosQuadrados += m2Pedido;
      analise[vendedor].valorForros += valorForroPedido;
      analise[vendedor].valorAcessorios += valorAcessoriosPedido;
      analise[vendedor].pedidos.push(p);
    });
    
    return Object.values(analise).map(v => ({
      ...v,
      margemMedia: v.valorTotal > 0 ? ((v.lucroTotal / v.valorTotal) * 100) : 0,
      ticketMedio: v.qtdPedidos > 0 ? (v.valorTotal / v.qtdPedidos) : 0,
      precoM2Medio: v.metrosQuadrados > 0 ? (v.valorTotal / v.metrosQuadrados) : 0,
      precoMedioM2Forros: v.metrosForros > 0 ? (v.valorTotal / v.metrosForros) : 0,
      percAcabamentos: v.valorForros > 0 ? ((v.valorAcessorios / v.valorForros) * 100) : 0
    }));
  };

  // Ordenar dados dos representantes
  const ordenarDados = (dados) => {
    return [...dados].sort((a, b) => {
      const valorA = a[ordenacao.coluna] || 0;
      const valorB = b[ordenacao.coluna] || 0;
      
      if (ordenacao.direcao === 'desc') {
        return valorB - valorA;
      }
      return valorA - valorB;
    });
  };

  const dadosRepresentantes = ordenarDados(analisePorRepresentante());

  // Totalizadores gerais
  const totalizadores = {
    qtdTotal: pedidosFiltrados.length,
    valorTotal: pedidosFiltrados.reduce((sum, p) => sum + (parseFloat(p.valor_tabela1) || 0) + (parseFloat(p.valor_tabela2) || 0) + (parseFloat(p.valor_produtos_especiais) || 0), 0),
    custoTotal: pedidosFiltrados.reduce((sum, p) => sum + (parseFloat(p.custo_tabela1) || 0) + (parseFloat(p.custo_tabela2) || 0), 0),
    metrosTotal: pedidosFiltrados.reduce((sum, p) => sum + calcularAreaPedido(p), 0),
    valorForrosTotal: pedidosFiltrados.reduce((sum, p) => sum + (parseFloat(p.valor_forro_geral) || 0), 0),
    valorAcessoriosTotal: pedidosFiltrados.reduce((sum, p) => sum + (parseFloat(p.valor_acessorios_geral) || 0), 0)
  };
  totalizadores.lucroTotal = totalizadores.valorTotal - totalizadores.custoTotal;
  totalizadores.margemMedia = totalizadores.valorTotal > 0 ? ((totalizadores.lucroTotal / totalizadores.valorTotal) * 100) : 0;
  totalizadores.ticketMedio = totalizadores.qtdTotal > 0 ? (totalizadores.valorTotal / totalizadores.qtdTotal) : 0;
  totalizadores.precoM2Medio = totalizadores.metrosTotal > 0 ? (totalizadores.valorTotal / totalizadores.metrosTotal) : 0;
  totalizadores.percAcabamentos = totalizadores.valorForrosTotal > 0 ? ((totalizadores.valorAcessoriosTotal / totalizadores.valorForrosTotal) * 100) : 0;

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <BarChart3 size={16} style={{ color: CORES.azulMedio }} />
          Dashboard Analítico de Vendedores
        </h3>
        
        <button
          onClick={() => setMostrarFiltrosAnalitico(!mostrarFiltrosAnalitico)}
          className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all duration-200 border border-gray-200"
        >
          <Filter size={14} style={{ color: CORES.azulMedio }} />
          <span className="text-sm font-medium text-gray-600">Filtros</span>
          {mostrarFiltrosAnalitico ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      {/* Filtros */}
      {mostrarFiltrosAnalitico && (
        <div className="bg-gray-50 rounded-xl p-4 mb-4 border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-1 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Representante</label>
              <select
                value={responsavelSelecionado}
                onChange={(e) => setResponsavelSelecionado(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="todos">Todos os representantes</option>
                {listaResponsaveis.map(resp => (
                  <option key={resp} value={resp}>{resp}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Cards de Totalizadores */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 hover:bg-white hover:shadow-md hover:border-blue-200 transition-all duration-300 cursor-default">
          <div className="flex items-center gap-2 mb-1">
            <ShoppingCart size={14} style={{ color: CORES.azulMedio }} />
            <span className="text-xs font-medium text-gray-400">Total de Pedidos</span>
          </div>
          <p className="text-xl font-bold" style={{ color: CORES.azulEscuro }}>{totalizadores.qtdTotal}</p>
        </div>

        <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 hover:bg-white hover:shadow-md hover:border-green-200 transition-all duration-300 cursor-default">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign size={14} style={{ color: CORES.verde }} />
            <span className="text-xs font-medium text-gray-400">Valor Total</span>
          </div>
          <p className="text-xl font-bold" style={{ color: CORES.verde }}>{formatarMoeda(totalizadores.valorTotal)}</p>
        </div>

        <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 hover:bg-white hover:shadow-md hover:border-amber-200 transition-all duration-300 cursor-default">
          <div className="flex items-center gap-2 mb-1">
            <Percent size={14} style={{ color: CORES.amarelo }} />
            <span className="text-xs font-medium text-gray-400">Margem Média</span>
          </div>
          <p className="text-xl font-bold" style={{ color: CORES.amarelo }}>{formatarPorcentagem(totalizadores.margemMedia)}</p>
        </div>

        <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 hover:bg-white hover:shadow-md hover:border-cyan-200 transition-all duration-300 cursor-default">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={14} style={{ color: CORES.ciano }} />
            <span className="text-xs font-medium text-gray-400">Ticket Médio</span>
          </div>
          <p className="text-xl font-bold" style={{ color: CORES.ciano }}>{formatarMoeda(totalizadores.ticketMedio)}</p>
        </div>
      </div>

      {/* Tabela Analítica */}
      <div className="mt-4">
        <h4 className="text-xs font-medium text-gray-400 mb-3 flex items-center gap-2">
          <Users size={14} style={{ color: CORES.azulMedio }} />
          Análise por Representante
        </h4>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-3 px-2 font-medium text-gray-400 text-xs">#</th>
                <th className="text-left py-3 px-2 font-medium text-gray-400 text-xs">Representante</th>
                <th className="text-center py-3 px-2 font-medium text-gray-400 text-xs">
                  <BotaoOrdenacao coluna="qtdPedidos">Pedidos</BotaoOrdenacao>
                </th>
                <th className="text-right py-3 px-2 font-medium text-gray-400 text-xs">
                  <div className="flex justify-end">
                    <BotaoOrdenacao coluna="valorTotal">Receita</BotaoOrdenacao>
                  </div>
                </th>
                <th className="text-right py-3 px-2 font-medium text-gray-400 text-xs">
                  <div className="flex justify-end">
                    <BotaoOrdenacao coluna="custoTotal">Custo</BotaoOrdenacao>
                  </div>
                </th>
                <th className="text-right py-3 px-2 font-medium text-gray-400 text-xs">
                  <div className="flex justify-end">
                    <BotaoOrdenacao coluna="lucroTotal">Lucro</BotaoOrdenacao>
                  </div>
                </th>
                <th className="text-right py-3 px-2 font-medium text-gray-400 text-xs">
                  <div className="flex justify-end">
                    <BotaoOrdenacao coluna="margemMedia">Margem</BotaoOrdenacao>
                  </div>
                </th>
                <th className="text-right py-3 px-2 font-medium text-gray-400 text-xs">
                  <div className="flex justify-end">
                    <BotaoOrdenacao coluna="ticketMedio">Ticket Médio</BotaoOrdenacao>
                  </div>
                </th>
                <th className="text-right py-3 px-2 font-medium text-gray-400 text-xs">
                  <div className="flex justify-end">
                    <BotaoOrdenacao coluna="precoMedioM2Forros">Preço Médio/M²</BotaoOrdenacao>
                  </div>
                </th>
                <th className="text-right py-3 px-2 font-medium text-gray-400 text-xs">
                  <div className="flex justify-end">
                    <BotaoOrdenacao coluna="percAcabamentos">% Acabamentos</BotaoOrdenacao>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {dadosRepresentantes.map((rep, index) => (
                <tr key={index} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="py-3 px-2">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shadow-sm"
                      style={{ backgroundColor: index === 0 ? CORES.amarelo : index === 1 ? CORES.cinza : index === 2 ? '#D97706' : '#E5E7EB', color: index > 2 ? '#666' : '#fff' }}
                    >
                      {index + 1}
                    </span>
                  </td>
                  <td className="py-3 px-2">
                    <span className="font-medium text-gray-700">{rep.nome}</span>
                  </td>
                  <td className="py-3 px-2 text-center">
                    <span className="px-2 py-0.5 rounded-full text-xs text-white shadow-sm" style={{ backgroundColor: CORES.azulMedio }}>
                      {rep.qtdPedidos}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-right">
                    <span className="font-medium" style={{ color: CORES.azulMedio }}>{formatarMoeda(rep.valorTotal)}</span>
                  </td>
                  <td className="py-3 px-2 text-right">
                    <span style={{ color: CORES.vermelho }}>{formatarMoeda(rep.custoTotal)}</span>
                  </td>
                  <td className="py-3 px-2 text-right">
                    <span className="font-medium" style={{ color: CORES.verde }}>
                      {formatarMoeda(rep.lucroTotal)}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-right">
                    <span className={`px-2 py-0.5 rounded-lg text-xs font-medium ${
                      rep.margemMedia >= 15 ? 'bg-green-50 text-green-600' : 
                      rep.margemMedia >= 10 ? 'bg-amber-50 text-amber-600' : 
                      rep.margemMedia >= 0 ? 'bg-orange-50 text-orange-600' : 
                      'bg-red-50 text-red-600'
                    }`}>
                      {formatarPorcentagem(rep.margemMedia)}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-right">
                    <span className="font-medium" style={{ color: CORES.ciano }}>{formatarMoeda(rep.ticketMedio)}</span>
                  </td>
                  <td className="py-3 px-2 text-right">
                    <span className="font-medium" style={{ color: CORES.laranja }}>{formatarMoeda(rep.precoMedioM2Forros)}</span>
                  </td>
                  <td className="py-3 px-2 text-right">
                    <span className={`px-2 py-0.5 rounded-lg text-xs font-medium ${
                      rep.percAcabamentos >= 20 ? 'bg-green-50 text-green-600' : 
                      rep.percAcabamentos >= 10 ? 'bg-amber-50 text-amber-600' : 
                      'bg-gray-50 text-gray-600'
                    }`}>
                      {formatarPorcentagem(rep.percAcabamentos)}
                    </span>
                  </td>
                </tr>
              ))}
              
              {dadosRepresentantes.length === 0 && (
                <tr>
                  <td colSpan="10" className="py-8 text-center text-gray-400">
                    Nenhum dado encontrado para o período selecionado
                  </td>
                </tr>
              )}
              
              {/* Linha de Totais */}
              {dadosRepresentantes.length > 1 && (
                <tr className="bg-gray-50 font-bold border-t border-gray-200">
                  <td colSpan="2" className="py-3 px-2 text-gray-700">TOTAL GERAL</td>
                  <td className="py-3 px-2 text-center">
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-blue-600 text-white shadow-sm">
                      {totalizadores.qtdTotal}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-right" style={{ color: CORES.azulMedio }}>
                    {formatarMoeda(totalizadores.valorTotal)}
                  </td>
                  <td className="py-3 px-2 text-right" style={{ color: CORES.vermelho }}>
                    {formatarMoeda(totalizadores.custoTotal)}
                  </td>
                  <td className="py-3 px-2 text-right" style={{ color: CORES.verde }}>
                    {formatarMoeda(totalizadores.lucroTotal)}
                  </td>
                  <td className="py-3 px-2 text-right">
                    <span className={`px-2 py-0.5 rounded-lg text-xs font-bold ${
                      totalizadores.margemMedia >= 15 ? 'bg-green-50 text-green-600' : 
                      totalizadores.margemMedia >= 10 ? 'bg-amber-50 text-amber-600' : 
                      'bg-orange-50 text-orange-600'
                    }`}>
                      {formatarPorcentagem(totalizadores.margemMedia)}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-right" style={{ color: CORES.ciano }}>
                    {formatarMoeda(totalizadores.ticketMedio)}
                  </td>
                  <td className="py-3 px-2 text-right" style={{ color: CORES.laranja }}>
                    {formatarMoeda(totalizadores.precoM2Medio)}
                  </td>
                  <td className="py-3 px-2 text-right">
                    <span className={`px-2 py-0.5 rounded-lg text-xs font-bold ${
                      totalizadores.percAcabamentos >= 20 ? 'bg-green-50 text-green-600' : 
                      totalizadores.percAcabamentos >= 10 ? 'bg-amber-50 text-amber-600' : 
                      'bg-orange-50 text-orange-600'
                    }`}>
                      {formatarPorcentagem(totalizadores.percAcabamentos)}
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ==================== COMPONENTE DASHBOARD ANALÍTICO DE PRODUTOS ====================
const DashboardAnaliticoProdutos = ({ pedidosFiltrados: pedidosFiltradosPai, formatarMoeda, formatarPorcentagem, CORES }) => {
  const [loading, setLoading] = useState(true);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [dadosProdutos, setDadosProdutos] = useState([]);
  const [custosProdutos, setCustosProdutos] = useState([]);
  
  // Filtros (removidos Tipo e Período - agora vêm do Dashboard principal)
  const [filtros, setFiltros] = useState({
    estado: 'todos',
    grupoProduto: 'todos',
    familiaProduto: 'todos',
    tipoTabela: 'todos'
  });

  // Estado para ordenação da tabela
  const [ordenacao, setOrdenacao] = useState({ coluna: 'receitaTotal', direcao: 'desc' });

  // Listas para os filtros
  const [listaEstados, setListaEstados] = useState([]);
  const [listaGrupos, setListaGrupos] = useState([]);
  const [listaFamilias, setListaFamilias] = useState([]);

  // Função para alternar ordenação
  const alternarOrdenacao = (coluna) => {
    setOrdenacao(prev => ({
      coluna,
      direcao: prev.coluna === coluna && prev.direcao === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Componente do botão de ordenação
  const BotaoOrdenacao = ({ coluna, children }) => (
    <button
      onClick={() => alternarOrdenacao(coluna)}
      className="flex items-center gap-1 hover:text-blue-600 transition-colors duration-200 group"
    >
      {children}
      <span className={`transition-all duration-200 ${ordenacao.coluna === coluna ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}`}>
        {ordenacao.coluna === coluna ? (
          ordenacao.direcao === 'desc' ? (
            <ArrowDown size={14} className="text-blue-600" />
          ) : (
            <ArrowUp size={14} className="text-blue-600" />
          )
        ) : (
          <ArrowDown size={14} />
        )}
      </span>
    </button>
  );

  // Carregar dados
  useEffect(() => {
    carregarDados();
  }, [filtros, pedidosFiltradosPai]);

  // Carregar custos de produtos e listas de filtros
  useEffect(() => {
    const carregarCustos = async () => {
      try {
        const data = await custoAPI.listarTodos();
        if (data) {
          setCustosProdutos(data);
          
          // Extrair listas únicas para filtros
          const estados = [...new Set(data.map(c => c.estado))].filter(Boolean).sort();
          const grupos = [...new Set(data.map(c => c.grupo_produto))].filter(Boolean).sort();
          const familias = [...new Set(data.map(c => c.familia_produto))].filter(Boolean).sort();
          
          setListaEstados(estados);
          setListaGrupos(grupos);
          setListaFamilias(familias);
        }
      } catch (error) {
        console.error('Erro ao carregar custos:', error);
      }
    };
    carregarCustos();
  }, []);

  const carregarDados = async () => {
    setLoading(true);
    try {
      // Buscar itens de forro e acessório via API (período amplo, filtragem por pedidos do Dashboard principal)
      const { forros, acessorios } = await pedidoAPI.buscarItensDashboard('1ano', 'todos');

      // IDs dos pedidos filtrados pelo Dashboard principal
      const pedidoIds = new Set(pedidosFiltradosPai.map(p => p.id));

      // Processar dados
      const analise = {};

      // Processar forros (filtrar apenas itens dos pedidos filtrados)
      (forros || []).filter(item => pedidoIds.has(item.pedido_id)).forEach(item => {
        const estadoCliente = item.cliente_estado || 'Não informado';
        const familia = item.familia_produto || 'Não informado';
        const tipoTabela = item.tipo_tabela || 'Tabela 1';
        
        // Aplicar filtros
        if (filtros.estado !== 'todos' && estadoCliente !== filtros.estado) return;
        if (filtros.grupoProduto !== 'todos' && filtros.grupoProduto !== 'Forro') return;
        if (filtros.familiaProduto !== 'todos' && familia !== filtros.familiaProduto) return;
        if (filtros.tipoTabela !== 'todos' && tipoTabela !== filtros.tipoTabela) return;

        const chave = `${familia}`;
        
        if (!analise[chave]) {
          analise[chave] = {
            familiaProduto: familia,
            grupoProduto: 'Forro',
            qtdVendas: 0,
            pedidosUnicos: new Set(),
            metrosQuadrados: 0,
            receitaTotal: 0,
            custoTotal: 0
          };
        }

        analise[chave].qtdVendas += 1;
        analise[chave].pedidosUnicos.add(item.pedido_id);
        analise[chave].metrosQuadrados += parseFloat(item.m2 || 0);
        analise[chave].receitaTotal += parseFloat(item.total || 0);
        // Custo = m2 * custo_unitario
        const custoItem = parseFloat(item.m2 || 0) * parseFloat(item.custo_unitario || 0);
        analise[chave].custoTotal += custoItem;
      });

      // Processar acessórios (filtrar apenas itens dos pedidos filtrados)
      (acessorios || []).filter(item => pedidoIds.has(item.pedido_id)).forEach(item => {
        const estadoCliente = item.pedidos?.clientes?.estado || 'Não informado';
        const familia = item.familia_produto || item.produto || 'Não informado';
        const tipoTabela = item.tipo_tabela || 'Tabela 1';
        
        // Aplicar filtros
        if (filtros.estado !== 'todos' && estadoCliente !== filtros.estado) return;
        if (filtros.grupoProduto !== 'todos' && filtros.grupoProduto !== 'Acessórios') return;
        if (filtros.familiaProduto !== 'todos' && familia !== filtros.familiaProduto) return;
        if (filtros.tipoTabela !== 'todos' && tipoTabela !== filtros.tipoTabela) return;

        const chave = `${familia}`;
        
        if (!analise[chave]) {
          analise[chave] = {
            familiaProduto: familia,
            grupoProduto: 'Acessórios',
            qtdVendas: 0,
            pedidosUnicos: new Set(),
            metrosQuadrados: 0,
            receitaTotal: 0,
            custoTotal: 0
          };
        }

        analise[chave].qtdVendas += 1;
        analise[chave].pedidosUnicos.add(item.pedido_id);
        analise[chave].metrosQuadrados += parseFloat(item.quantidade || 0);
        analise[chave].receitaTotal += parseFloat(item.total || 0);
        // Custo = quantidade * custo_unitario
        const custoItem = parseFloat(item.quantidade || 0) * parseFloat(item.custo_unitario || 0);
        analise[chave].custoTotal += custoItem;
      });

      // Converter para array e calcular métricas
      const dadosProcessados = Object.values(analise).map(item => ({
        ...item,
        qtdPedidos: item.pedidosUnicos.size,
        lucroTotal: item.receitaTotal - item.custoTotal,
        margemMedia: item.receitaTotal > 0 ? ((item.receitaTotal - item.custoTotal) / item.receitaTotal) * 100 : 0,
        precoM2: item.metrosQuadrados > 0 ? item.receitaTotal / item.metrosQuadrados : 0,
        custoM2: item.metrosQuadrados > 0 ? item.custoTotal / item.metrosQuadrados : 0,
        ticketMedio: item.pedidosUnicos.size > 0 ? item.receitaTotal / item.pedidosUnicos.size : 0
      }));

      setDadosProdutos(dadosProcessados);
    } catch (error) {
      console.error('Erro ao carregar dados de produtos:', error);
    }
    setLoading(false);
  };

  // Ordenar dados
  const dadosOrdenados = [...dadosProdutos].sort((a, b) => {
    const valorA = a[ordenacao.coluna] || 0;
    const valorB = b[ordenacao.coluna] || 0;
    return ordenacao.direcao === 'desc' ? valorB - valorA : valorA - valorB;
  });

  // Totalizadores
  const totalizadores = {
    qtdVendas: dadosProdutos.reduce((sum, p) => sum + p.qtdVendas, 0),
    qtdPedidos: dadosProdutos.reduce((sum, p) => sum + p.qtdPedidos, 0),
    metrosTotal: dadosProdutos.filter(p => p.grupoProduto === 'Forro').reduce((sum, p) => sum + p.metrosQuadrados, 0),
    qtdAcessorios: dadosProdutos.filter(p => p.grupoProduto === 'Acessórios').reduce((sum, p) => sum + p.metrosQuadrados, 0),
    receitaTotal: dadosProdutos.reduce((sum, p) => sum + p.receitaTotal, 0),
    custoTotal: dadosProdutos.reduce((sum, p) => sum + p.custoTotal, 0)
  };
  totalizadores.lucroTotal = totalizadores.receitaTotal - totalizadores.custoTotal;
  totalizadores.margemMedia = totalizadores.receitaTotal > 0 ? (totalizadores.lucroTotal / totalizadores.receitaTotal) * 100 : 0;
  // Preço/m² calculado apenas com receita de forros dividido por m² de forros
  const receitaForros = dadosProdutos.filter(p => p.grupoProduto === 'Forro').reduce((sum, p) => sum + p.receitaTotal, 0);
  totalizadores.precoM2 = totalizadores.metrosTotal > 0 ? receitaForros / totalizadores.metrosTotal : 0;
  totalizadores.ticketMedio = totalizadores.qtdPedidos > 0 ? totalizadores.receitaTotal / totalizadores.qtdPedidos : 0;

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <PieChartIcon size={16} style={{ color: CORES.verde }} />
          Dashboard Analítico de Produtos
        </h3>
        
        <button
          onClick={() => setMostrarFiltros(!mostrarFiltros)}
          className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all duration-200 border border-gray-200"
        >
          <Filter size={14} style={{ color: CORES.verde }} />
          <span className="text-sm font-medium text-gray-600">Filtros</span>
          {mostrarFiltros ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      {/* Filtros */}
      {mostrarFiltros && (
        <div className="bg-gray-50 rounded-xl p-4 mb-4 border border-gray-100">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Grupo Produto</label>
              <select
                value={filtros.grupoProduto}
                onChange={(e) => setFiltros({ ...filtros, grupoProduto: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="todos">Todos os grupos</option>
                {listaGrupos.map(grupo => (
                  <option key={grupo} value={grupo}>{grupo}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Família Produto</label>
              <select
                value={filtros.familiaProduto}
                onChange={(e) => setFiltros({ ...filtros, familiaProduto: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="todos">Todas as famílias</option>
                {listaFamilias.map(familia => (
                  <option key={familia} value={familia}>{familia}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Tipo Tabela</label>
              <select
                value={filtros.tipoTabela}
                onChange={(e) => setFiltros({ ...filtros, tipoTabela: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="todos">Todas as tabelas</option>
                <option value="Tabela 1">Tabela 1</option>
                <option value="Tabela 2">Tabela 2</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Cards de Totalizadores - Primeira Linha */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 hover:bg-white hover:shadow-md hover:border-cyan-200 transition-all duration-300 cursor-default">
          <div className="flex items-center gap-2 mb-1">
            <Activity size={14} style={{ color: CORES.ciano }} />
            <span className="text-xs font-medium text-gray-400">Total m² (Forros)</span>
          </div>
          <p className="text-xl font-bold" style={{ color: CORES.ciano }}>{totalizadores.metrosTotal.toFixed(2).replace('.', ',')} m²</p>
        </div>

        <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 hover:bg-white hover:shadow-md hover:border-orange-200 transition-all duration-300 cursor-default">
          <div className="flex items-center gap-2 mb-1">
            <Zap size={14} style={{ color: CORES.laranja }} />
            <span className="text-xs font-medium text-gray-400">Preço/m²</span>
          </div>
          <p className="text-xl font-bold" style={{ color: CORES.laranja }}>{formatarMoeda(totalizadores.precoM2)}</p>
        </div>

        <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 hover:bg-white hover:shadow-md hover:border-purple-200 transition-all duration-300 cursor-default">
          <div className="flex items-center gap-2 mb-1">
            <Package size={14} style={{ color: '#9333ea' }} />
            <span className="text-xs font-medium text-gray-400">Qtd. Acabamentos</span>
          </div>
          <p className="text-xl font-bold" style={{ color: '#9333ea' }}>{totalizadores.qtdAcessorios.toFixed(0)}</p>
        </div>

        <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 hover:bg-white hover:shadow-md hover:border-blue-200 transition-all duration-300 cursor-default">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={14} style={{ color: CORES.azulMedio }} />
            <span className="text-xs font-medium text-gray-400">Ticket Médio</span>
          </div>
          <p className="text-xl font-bold" style={{ color: CORES.azulMedio }}>{formatarMoeda(totalizadores.ticketMedio)}</p>
        </div>

        <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 hover:bg-white hover:shadow-md hover:border-amber-200 transition-all duration-300 cursor-default">
          <div className="flex items-center gap-2 mb-1">
            <Percent size={14} style={{ color: CORES.amarelo }} />
            <span className="text-xs font-medium text-gray-400">Margem Média</span>
          </div>
          <p className="text-xl font-bold" style={{ color: CORES.amarelo }}>{formatarPorcentagem(totalizadores.margemMedia)}</p>
        </div>
      </div>

      {/* Segunda linha de cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 hover:bg-white hover:shadow-md hover:border-green-200 transition-all duration-300 cursor-default">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign size={14} style={{ color: CORES.verde }} />
            <span className="text-xs font-medium text-gray-400">Receita Total</span>
          </div>
          <p className="text-xl font-bold" style={{ color: CORES.verde }}>{formatarMoeda(totalizadores.receitaTotal)}</p>
        </div>

        <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 hover:bg-white hover:shadow-md hover:border-red-200 transition-all duration-300 cursor-default">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign size={14} style={{ color: CORES.vermelho }} />
            <span className="text-xs font-medium text-gray-400">Custo Total</span>
          </div>
          <p className="text-xl font-bold" style={{ color: CORES.vermelho }}>{formatarMoeda(totalizadores.custoTotal)}</p>
        </div>

        <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 hover:bg-white hover:shadow-md hover:border-green-200 transition-all duration-300 cursor-default">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={14} style={{ color: CORES.verde }} />
            <span className="text-xs font-medium text-gray-400">Lucro Total</span>
          </div>
          <p className="text-xl font-bold" style={{ color: CORES.verde }}>{formatarMoeda(totalizadores.lucroTotal)}</p>
        </div>
      </div>

      {/* Tabela Analítica */}
      <div className="mt-4">
        <h4 className="text-xs font-medium text-gray-400 mb-3 flex items-center gap-2">
          <PieChartIcon size={14} style={{ color: CORES.verde }} />
          Análise por Produto
        </h4>
        
        {loading ? (
          <div className="py-8 text-center">
            <Loading texto="Carregando dados de produtos..." tamanho="md" fullScreen={false} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-3 px-2 font-medium text-gray-400 text-xs">#</th>
                  <th className="text-left py-3 px-2 font-medium text-gray-400 text-xs">Produto</th>
                  <th className="text-center py-3 px-2 font-medium text-gray-400 text-xs">
                    <BotaoOrdenacao coluna="qtdPedidos">Vendas</BotaoOrdenacao>
                  </th>
                  <th className="text-right py-3 px-2 font-medium text-gray-400 text-xs">
                    <div className="flex justify-end">
                      <BotaoOrdenacao coluna="metrosQuadrados">m²/qtde</BotaoOrdenacao>
                    </div>
                  </th>
                  <th className="text-right py-3 px-2 font-medium text-gray-400 text-xs">
                    <div className="flex justify-end">
                      <BotaoOrdenacao coluna="receitaTotal">Receita</BotaoOrdenacao>
                    </div>
                  </th>
                  <th className="text-right py-3 px-2 font-medium text-gray-400 text-xs">
                    <div className="flex justify-end">
                      <BotaoOrdenacao coluna="custoTotal">Custo</BotaoOrdenacao>
                    </div>
                  </th>
                  <th className="text-right py-3 px-2 font-medium text-gray-400 text-xs">
                    <div className="flex justify-end">
                      <BotaoOrdenacao coluna="lucroTotal">Lucro</BotaoOrdenacao>
                    </div>
                  </th>
                  <th className="text-right py-3 px-2 font-medium text-gray-400 text-xs">
                    <div className="flex justify-end">
                      <BotaoOrdenacao coluna="margemMedia">Margem</BotaoOrdenacao>
                    </div>
                  </th>
                  <th className="text-right py-3 px-2 font-medium text-gray-400 text-xs">
                    <div className="flex justify-end">
                      <BotaoOrdenacao coluna="precoM2">R$/m²</BotaoOrdenacao>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {dadosOrdenados.map((prod, index) => (
                  <tr key={index} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-2">
                      <span 
                        className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shadow-sm"
                        style={{ backgroundColor: index === 0 ? CORES.amarelo : index === 1 ? CORES.cinza : index === 2 ? '#D97706' : '#E5E7EB', color: index > 2 ? '#666' : '#fff' }}
                      >
                        {index + 1}
                      </span>
                    </td>
                    <td className="py-3 px-2">
                      <div>
                        <span className="font-medium text-gray-700">{prod.familiaProduto}</span>
                        <span className="block text-xs text-gray-400">{prod.grupoProduto}</span>
                      </div>
                    </td>
                    <td className="py-3 px-2 text-center">
                      <span className="px-2 py-0.5 rounded-full text-xs text-white shadow-sm" style={{ backgroundColor: CORES.azulMedio }}>
                        {prod.qtdPedidos}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span className="font-medium" style={{ color: CORES.ciano }}>
                        {prod.metrosQuadrados.toFixed(2).replace('.', ',')}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span className="font-medium" style={{ color: CORES.verde }}>{formatarMoeda(prod.receitaTotal)}</span>
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span style={{ color: CORES.vermelho }}>{formatarMoeda(prod.custoTotal)}</span>
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span className="font-medium" style={{ color: CORES.verde }}>
                        {formatarMoeda(prod.lucroTotal)}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span className={`px-2 py-0.5 rounded-lg text-xs font-medium ${
                        prod.margemMedia >= 15 ? 'bg-green-50 text-green-600' : 
                        prod.margemMedia >= 10 ? 'bg-amber-50 text-amber-600' : 
                        prod.margemMedia >= 0 ? 'bg-orange-50 text-orange-600' : 
                        'bg-red-50 text-red-600'
                      }`}>
                        {formatarPorcentagem(prod.margemMedia)}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span className="font-medium" style={{ color: CORES.laranja }}>{formatarMoeda(prod.precoM2)}</span>
                    </td>
                  </tr>
                ))}
                
                {dadosOrdenados.length === 0 && (
                  <tr>
                    <td colSpan="9" className="py-8 text-center text-gray-400">
                      Nenhum dado encontrado para os filtros selecionados
                    </td>
                  </tr>
                )}
                
                {/* Linha de Totais */}
                {dadosOrdenados.length > 0 && (
                  <tr className="bg-gray-50 font-bold border-t border-gray-200">
                    <td colSpan="2" className="py-3 px-2 text-gray-700">TOTAL GERAL</td>
                    <td className="py-3 px-2 text-center">
                      <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-green-600 text-white shadow-sm">
                        {totalizadores.qtdPedidos}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-right" style={{ color: CORES.ciano }}>
                      {totalizadores.metrosTotal.toFixed(2).replace('.', ',')}
                    </td>
                    <td className="py-3 px-2 text-right" style={{ color: CORES.verde }}>
                      {formatarMoeda(totalizadores.receitaTotal)}
                    </td>
                    <td className="py-3 px-2 text-right" style={{ color: CORES.vermelho }}>
                      {formatarMoeda(totalizadores.custoTotal)}
                    </td>
                    <td className="py-3 px-2 text-right" style={{ color: CORES.verde }}>
                      {formatarMoeda(totalizadores.lucroTotal)}
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span className={`px-2 py-0.5 rounded-lg text-xs font-bold ${
                        totalizadores.margemMedia >= 15 ? 'bg-green-50 text-green-600' : 
                        totalizadores.margemMedia >= 10 ? 'bg-amber-50 text-amber-600' : 
                        'bg-orange-50 text-orange-600'
                      }`}>
                        {formatarPorcentagem(totalizadores.margemMedia)}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-right" style={{ color: CORES.laranja }}>
                      {formatarMoeda(totalizadores.precoM2)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== COMPONENTE DASHBOARD DE CLIENTES ====================
const DashboardClientes = ({ formatarMoeda, CORES }) => {
  const { usuario: usuarioLogado } = useAuth();
  const [loading, setLoading] = useState(true);
  const [todosClientes, setTodosClientes] = useState([]); // Todos os clientes para estatísticas
  const [clientesPeriodo, setClientesPeriodo] = useState([]); // Clientes filtrados por período para o gráfico
  const [periodo, setPeriodo] = useState('7dias');

  // Verificar se pode ver todos os clientes (Admin, Diretoria, Administrativo)
  const podeVerTodosClientes = () => {
    if (!usuarioLogado) return false;
    const dep = (usuarioLogado.departamento || '').toLowerCase().trim();
    return ['admin', 'diretoria', 'administrativo', 'administrador'].includes(dep);
  };

  // Botões de período
  const botoesPeriodo = [
    { id: '7dias', label: '7D' },
    { id: '30dias', label: '30D' },
    { id: '60dias', label: '60D' },
    { id: '90dias', label: '90D' },
    { id: '6meses', label: '6M' },
    { id: '1ano', label: '1A' }
  ];

  // Status de cliente disponíveis
  const statusCliente = [
    { value: 'ATIVO', label: 'Cliente Ativo', cor: '#10B981', icon: UserCheck },
    { value: 'CADASTRADO', label: 'Cliente Cadastrado', cor: '#3B82F6', icon: UserPlus },
    { value: 'INATIVO', label: 'Cliente Inativo', cor: '#F59E0B', icon: Clock },
    { value: 'EXCLUIDO', label: 'Cliente Excluído', cor: '#DC2626', icon: Ban },
    { value: 'APROVADO', label: 'Cadastro Aprovado', cor: '#22C55E', icon: CheckCircle },
    { value: 'EM_ANALISE', label: 'Cadastro em Análise', cor: '#EAB308', icon: Clock },
    { value: 'PENDENTE', label: 'Cadastro em Análise', cor: '#EAB308', icon: Clock, hidden: true },
    { value: 'REJEITADO', label: 'Cadastro Rejeitado', cor: '#EF4444', icon: UserX }
  ];

  // Carregar dados iniciais (todos os clientes)
  useEffect(() => {
    carregarTodosClientes();
  }, [usuarioLogado]);

  // Atualizar clientes do período quando o período muda
  useEffect(() => {
    filtrarClientesPorPeriodo();
  }, [periodo, todosClientes]);

  const carregarTodosClientes = async () => {
    setLoading(true);
    try {
      // Usar listarPorVendedor - o backend já filtra baseado no departamento do usuário
      // Admin/Diretoria/Administrativo vê todos, Vendas/Representante vê apenas os seus
      const data = await clienteAPI.listarPorVendedor();
      setTodosClientes(data || []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
    setLoading(false);
  };

  const filtrarClientesPorPeriodo = () => {
    const hoje = new Date();
    const dias = {
      '7dias': 7,
      '30dias': 30,
      '60dias': 60,
      '90dias': 90,
      '6meses': 180,
      '1ano': 365
    };
    const diasFiltro = dias[periodo] || 7;
    const dataInicio = new Date(hoje);
    dataInicio.setDate(dataInicio.getDate() - diasFiltro);

    const clientesFiltrados = todosClientes.filter(c => {
      if (!c.created_at) return false;
      return new Date(c.created_at) >= dataInicio;
    });

    setClientesPeriodo(clientesFiltrados);
  };

  // Obter o status real do cliente (pode estar em diferentes campos)
  const getStatusCliente = (cliente) => {
    return cliente.status_calculado || cliente.status_cadastro || cliente.status || 'CADASTRADO';
  };

  // Calcular estatísticas por status (usando TODOS os clientes, não filtrado por período)
  const estatisticasPorStatus = () => {
    const stats = {};
    statusCliente.forEach(s => {
      if (s.value === 'EM_ANALISE') {
        // Contar também PENDENTE como EM_ANALISE
        stats[s.value] = todosClientes.filter(c => {
          const status = getStatusCliente(c);
          return status === 'EM_ANALISE' || status === 'PENDENTE';
        }).length;
      } else if (s.value !== 'PENDENTE') {
        stats[s.value] = todosClientes.filter(c => getStatusCliente(c) === s.value).length;
      }
    });
    return stats;
  };

  const stats = estatisticasPorStatus();
  const totalClientes = todosClientes.length;

  // Dados para gráfico por dia (usando clientes do PERÍODO selecionado)
  const dadosGrafico = () => {
    const dias = {};
    const hoje = new Date();
    const diasConfig = {
      '7dias': 7,
      '30dias': 30,
      '60dias': 60,
      '90dias': 90,
      '6meses': 180,
      '1ano': 365
    };
    const diasMostrar = diasConfig[periodo] || 7;
    
    for (let i = diasMostrar - 1; i >= 0; i--) {
      const data = new Date(hoje);
      data.setDate(data.getDate() - i);
      const chave = data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
      dias[chave] = 0;
    }
    
    clientesPeriodo.forEach(c => {
      if (c.created_at) {
        const data = new Date(c.created_at);
        const chave = data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
        if (dias.hasOwnProperty(chave)) {
          dias[chave]++;
        }
      }
    });
    
    return Object.entries(dias).map(([data, total]) => ({ name: data, total }));
  };

  // Card de estatística individual
  const CardStatus = ({ config, value }) => {
    const Icon = config.icon;
    return (
      <div 
        className="bg-white rounded-xl p-3 border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-default group"
        style={{ borderLeftWidth: '4px', borderLeftColor: config.cor }}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] font-medium text-gray-400 mb-0.5 leading-tight">{config.label}</p>
            <p className="text-xl font-bold" style={{ color: config.cor }}>{value}</p>
          </div>
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
            style={{ backgroundColor: `${config.cor}20` }}
          >
            <Icon size={16} style={{ color: config.cor }} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <UserPlus size={16} style={{ color: CORES.azulMedio }} />
          Dashboard de Clientes Cadastrados
        </h3>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: CORES.azulMedio }}></div>
        </div>
      ) : (
        <>
          {/* Gráfico e Cards de Status */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Gráfico */}
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Calendar size={14} style={{ color: CORES.azulMedio }} />
                  Novos Clientes por Dia
                </h4>
                
                {/* Seletor de Período */}
                <div className="flex gap-0.5 bg-white p-1 rounded-xl border border-gray-200">
                  {botoesPeriodo.map((btn) => (
                    <button
                      key={btn.id}
                      onClick={() => setPeriodo(btn.id)}
                      className={`px-2 py-1 rounded-lg text-xs font-medium transition-all duration-200 ${
                        periodo === btn.id
                          ? 'bg-blue-500 text-white shadow-sm'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={dadosGrafico()}>
                  <defs>
                    <linearGradient id="colorClientes" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CORES.azulMedio} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={CORES.azulMedio} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis 
                    dataKey="name" 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fontSize: 9, fill: '#9CA3AF' }}
                    interval={periodo === '7dias' ? 0 : periodo === '30dias' ? 4 : periodo === '60dias' ? 9 : 'preserveStartEnd'}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fontSize: 10, fill: '#9CA3AF' }}
                    allowDecimals={false}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                      border: 'none', 
                      borderRadius: '12px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                    }}
                    formatter={(value) => [`${value} cliente(s)`, 'Cadastrados']}
                  />
                  <Area
                    type="monotone"
                    dataKey="total"
                    stroke={CORES.azulMedio}
                    strokeWidth={2}
                    fill="url(#colorClientes)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Cards de Status Detalhados */}
            <div className="grid grid-cols-2 gap-4 content-start">
              {/* Coluna Cliente */}
              <div className="space-y-3">
                <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Cliente</h5>
                {statusCliente.filter(s => !s.hidden && s.label.startsWith('Cliente')).map(config => (
                  <CardStatus key={config.value} config={config} value={stats[config.value] || 0} />
                ))}
              </div>
              
              {/* Coluna Cadastro */}
              <div className="space-y-3">
                <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Cadastro</h5>
                {statusCliente.filter(s => !s.hidden && s.label.startsWith('Cadastro')).map(config => (
                  <CardStatus key={config.value} config={config} value={stats[config.value] || 0} />
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const Dashboard = () => {
  const { usuario: usuarioLogado, isAdminOuDiretoria } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pedidos, setPedidos] = useState([]);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [periodoFinanceiro, setPeriodoFinanceiro] = useState('6meses');
  const [precoMedioM2Data, setPrecoMedioM2Data] = useState({ totalM2: 0, totalVendas: 0, precoMedioM2: 0 });

  const podeVerDashboardsAnaliticos = () => {
    // Somente Admin e Diretoria podem ver dashboards analíticos de vendedores e produtos
    if (!usuarioLogado) return false;
    const dep = (usuarioLogado.departamento || '').toLowerCase().trim();
    return ['admin', 'diretoria'].includes(dep);
  };
  
  const [filtros, setFiltros] = useState({
    dataInicio: '',
    dataFim: '',
    dataFaturamentoInicio: '',
    dataFaturamentoFim: '',
    responsavel: '',
    tabela: 'todas',  // 'todas', 'tabela1', 'tabela2'
    tipo: '',  // '', 'ORCAMENTO', 'PEDIDO'
    fase: '',  // '', 'AGUARDANDO_PROXIMA_ETAPA', 'ORCAMENTO_APROVADO', etc.
    classificacao: '',  // '', 'EXCELENTE', 'BOA VENDA', etc.
    rede: '',  // '', ou nome da rede
    produtosEspeciais: ''  // '', 'sim', 'nao'
  });
  const [listaResponsaveis, setListaResponsaveis] = useState([]);
  const [listaRedes, setListaRedes] = useState([]);

  const podeVerTodos = () => {
    if (!usuarioLogado) return true;
    const departamento = (usuarioLogado.departamento || '').toLowerCase().trim();
    // Admin, Diretoria, Administrativo ou departamento vazio = vê tudo
    const deptAcessoTotal = ['admin', 'diretoria', 'administrador', 'administrativo', 'todos', ''];
    return deptAcessoTotal.includes(departamento);
  };

  const podeVerVisaoAdmin = () => {
    if (!usuarioLogado) return false;
    const dep = (usuarioLogado.departamento || '').toLowerCase().trim();
    // Somente Admin e Diretoria vêem a visão financeira completa
    return dep === 'admin' || dep === 'diretoria';
  };

  const getNomeUsuario = () => {
    if (!usuarioLogado) return '';
    return usuarioLogado.nome || usuarioLogado.nome_completo || usuarioLogado.username || usuarioLogado.username_ldap || '';
  };

  useEffect(() => {
    if (usuarioLogado !== null) {
      carregarPedidos();
    }
  }, [usuarioLogado]);

  // Carregar preço médio por m² quando filtros mudam
  useEffect(() => {
    const carregarPrecoMedioM2 = async () => {
      try {
        const data = await pedidoAPI.buscarPrecoMedioM2('1ano', filtros.responsavel, filtros.tipo || 'todos');
        setPrecoMedioM2Data(data);
      } catch (error) {
        console.error('Erro ao carregar preço médio por m²:', error);
      }
    };
    carregarPrecoMedioM2();
  }, [filtros.responsavel, filtros.tipo]);

  const carregarPedidos = async () => {
    setLoading(true);
    try {
      // Log para debug

      // Query via API para dashboard
      const data = await pedidoAPI.buscarDadosDashboard('1ano');


      if (!data) {
        console.error('Erro ao carregar pedidos');
        setPedidos([]);
        setLoading(false);
        return;
      }

      const responsaveisUnicos = [...new Set(data?.map(p => p.vendedor).filter(v => v))];
      setListaResponsaveis(responsaveisUnicos.sort());

      // Extrair lista de redes únicas dos clientes
      const redesUnicas = [...new Set(data?.map(p => p.clientes?.rede).filter(r => r && r.trim() !== ''))];
      setListaRedes(redesUnicas.sort());

      // O backend já filtra por vendedor - não precisamos filtrar novamente
      setPedidos(data || []);

      // Carregar preço médio por m² inicial
      try {
        const precoMedioData = await pedidoAPI.buscarPrecoMedioM2('1ano', '', 'todos');
        setPrecoMedioM2Data(precoMedioData);
      } catch (error) {
        console.error('Erro ao carregar preço médio:', error);
      }
    } catch (error) {
      console.error('Erro:', error);
      setPedidos([]);
    }
    setLoading(false);
  };

  // Função para calcular status geral (movida para antes do filtro)
  const getStatusByScoreParaFiltro = (pontuacao) => {
    const p = Math.round((pontuacao || 0) * 100) / 100;
    if (p >= 40) return 'EXCELENTE';
    if (p >= 25) return 'BOA VENDA';
    if (p > 0) return 'VIÁVEL';
    if (p >= -20) return 'EM ANÁLISE';
    return 'INVIÁVEL';
  };

  const calcularStatusGeralParaFiltro = (p) => {
    if (p.status_geral) return p.status_geral;
    const valorTab1 = parseFloat(p.valor_tabela1) || 0;
    const valorTab2 = parseFloat(p.valor_tabela2) || 0;
    const temTab1 = valorTab1 > 0;
    const temTab2 = valorTab2 > 0;
    if (!temTab1 && !temTab2) return 'INVIÁVEL';
    const pontTab1 = temTab1 ? Math.min(parseFloat(p.pontuacao_tabela1) || 0, 50) : 0;
    const pontTab2 = temTab2 ? Math.min(parseFloat(p.pontuacao_tabela2) || 0, 50) : 0;
    return getStatusByScoreParaFiltro(pontTab1 + pontTab2);
  };

  const pedidosFiltrados = pedidos.filter(p => {
    let match = true;
    if (filtros.dataInicio && p.created_at) {
      match = new Date(p.created_at) >= new Date(filtros.dataInicio);
    }
    if (filtros.dataFim && p.created_at && match) {
      match = new Date(p.created_at) <= new Date(filtros.dataFim + 'T23:59:59');
    }
    if (filtros.dataFaturamentoInicio || filtros.dataFaturamentoFim) {
      // Só pedidos faturados são relevantes para este filtro
      if (p.fase_atual !== 'PEDIDO_FATURADO') {
        match = false;
      } else {
        // Usa faturado_data se existir, senão usa created_at como fallback
        const dataRefFaturamento = p.faturado_data || p.created_at;
        if (filtros.dataFaturamentoInicio && match) match = new Date(dataRefFaturamento) >= new Date(filtros.dataFaturamentoInicio);
        if (filtros.dataFaturamentoFim && match) match = new Date(dataRefFaturamento) <= new Date(filtros.dataFaturamentoFim + 'T23:59:59');
      }
    }
    if (filtros.responsavel && match) {
      match = p.vendedor === filtros.responsavel;
    }
    // Filtro por tipo (Orçamento/Pedido)
    if (filtros.tipo && match) {
      match = p.tipo_documento === filtros.tipo;
    }
    // Filtro por fase do fluxo
    if (filtros.fase && match) {
      if (filtros.fase === 'APROVADOS') {
        match = ['ORCAMENTO_APROVADO', 'APROVADO_COM_AJUSTES'].includes(p.fase_atual);
      } else if (filtros.fase === 'CANCELADOS_TODOS') {
        // Agrupa todos os tipos de cancelamento (igual à Consulta de Pedidos)
        const fasesCancelamento = ['ORCAMENTO_CANCELADO', 'ORCAMENTO_RECUSADO', 'PEDIDO_CANCELADO', 'PEDIDO_CANCELADO_POS_FATURAMENTO', 'CANCELADO'];
        match = fasesCancelamento.includes(p.fase_atual) || !!p.cancelado_por;
      } else {
        match = p.fase_atual === filtros.fase;
      }
    }
    // Filtro por classificação do pedido
    if (filtros.classificacao && match) {
      const statusGeral = calcularStatusGeralParaFiltro(p);
      match = statusGeral === filtros.classificacao;
    }
    // Filtro por rede
    if (filtros.rede && match) {
      match = p.clientes?.rede === filtros.rede;
    }
    // Filtro por produtos especiais
    if (filtros.produtosEspeciais && match) {
      const temProdutosEspeciais = parseFloat(p.valor_produtos_especiais) > 0;
      if (filtros.produtosEspeciais === 'sim') {
        match = temProdutosEspeciais;
      } else if (filtros.produtosEspeciais === 'nao') {
        match = !temProdutosEspeciais;
      }
    }
    return match;
  });

  // ==================== FORMATADORES ====================
  
  const formatarMoeda = (valor) => {
    const numero = Number(valor) || 0;
    return numero.toLocaleString('pt-BR', { 
      style: 'currency', 
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatarPorcentagem = (valor) => {
    const numero = Number(valor) || 0;
    return numero.toFixed(1).replace('.', ',') + '%';
  };

  // ==================== CÁLCULOS ====================
  
  const totalPedidos = pedidosFiltrados.length;
  const valorTotal = pedidosFiltrados.reduce((sum, p) => sum + (parseFloat(p.valor_tabela1) || 0) + (parseFloat(p.valor_tabela2) || 0) + (parseFloat(p.valor_produtos_especiais) || 0), 0);
  const ticketMedio = totalPedidos > 0 ? valorTotal / totalPedidos : 0;
  const custoTotal = pedidosFiltrados.reduce((sum, p) => sum + (parseFloat(p.custo_tabela1) || 0) + (parseFloat(p.custo_tabela2) || 0), 0);
  const lucroTotal = valorTotal - custoTotal;
  const margemMedia = valorTotal > 0 ? (lucroTotal / valorTotal) * 100 : 0;

  // ==================== LÓGICA CORRIGIDA DE STATUS ====================
  // Função para calcular o STATUS GERAL do pedido baseado na pontuação
  const getStatusByScore = (pontuacao) => {
    const p = Math.round((pontuacao || 0) * 100) / 100;
    if (p >= 40) return 'EXCELENTE';
    if (p >= 20) return 'BOA VENDA';
    if (p > 0) return 'VIÁVEL';
    if (p >= -20) return 'EM ANÁLISE';
    return 'INVIÁVEL';
  };

  // Função para calcular o status geral do pedido
  const calcularStatusGeralPedido = (p) => {
    // Usar status_geral diretamente do banco se existir
    if (p.status_geral) {
      return p.status_geral;
    }
    
    // Fallback: calcular se não tiver no banco (pedidos antigos)
    const valorTab1 = parseFloat(p.valor_tabela1) || 0;
    const valorTab2 = parseFloat(p.valor_tabela2) || 0;
    const temTab1 = valorTab1 > 0;
    const temTab2 = valorTab2 > 0;
    
    if (!temTab1 && !temTab2) return 'INVIÁVEL';
    
    // Pontuação de cada tabela com cap de 50
    const pontTab1 = temTab1 ? Math.min(parseFloat(p.pontuacao_tabela1) || 0, 50) : 0;
    const pontTab2 = temTab2 ? Math.min(parseFloat(p.pontuacao_tabela2) || 0, 50) : 0;
    
    // Pontuação geral = soma das pontuações
    const pontuacaoGeral = pontTab1 + pontTab2;
    
    return getStatusByScore(pontuacaoGeral);
  };

  // Função para determinar o status FINAL de uma tabela
  // Prioridade: 1) status_aprovacao (se existir) 2) status_tabela (viabilidade)
  const getStatusFinal = (statusTabela, statusAprovacao) => {
    // Se não tem status de tabela (sem itens), retorna null
    if (!statusTabela) return null;
    
    // Se tem status de aprovação, ele define o resultado final
    if (statusAprovacao === 'APROVADO') return 'APROVADO';
    if (statusAprovacao === 'RECUSADO') return 'RECUSADO';
    
    // Se não tem status de aprovação (auto-aprovados ou aguardando)
    return statusTabela;
  };

  // Função para converter status em valor numérico (para calcular média)
  const statusParaNumero = (status) => {
    const valores = {
      'EXCELENTE': 7,
      'BOA VENDA': 6,
      'VIÁVEL': 5,
      'APROVADO': 4,
      'EM ANÁLISE': 3,
      'REANÁLISE': 2,
      'INVIÁVEL': 1,
      'RECUSADO': 0
    };
    return valores[status] ?? -1;
  };

  // Função para converter número de volta para status
  const numeroParaStatus = (numero) => {
    if (numero >= 6.5) return 'EXCELENTE';
    if (numero >= 5.5) return 'BOA VENDA';
    if (numero >= 4.5) return 'VIÁVEL';
    if (numero >= 3.5) return 'APROVADO';
    if (numero >= 2.5) return 'EM ANÁLISE';
    if (numero >= 1.5) return 'REANÁLISE';
    if (numero >= 0.5) return 'INVIÁVEL';
    return 'RECUSADO';
  };

  // Contar status considerando o filtro de tabela
  const contarStatusTabelas = () => {
    const counts = {
      excelentes: 0,
      boaVenda: 0,
      viaveis: 0,
      emAnalise: 0,
      inviaveis: 0
    };

    pedidosFiltrados.forEach(p => {
      const statusFinal1 = getStatusFinal(p.status_tabela1, null);
      const statusFinal2 = getStatusFinal(p.status_tabela2, null);
      
      let statusParaContar = null;

      if (filtros.tabela === 'tabela1') {
        // Contar apenas Tabela 1
        statusParaContar = statusFinal1;
      } else if (filtros.tabela === 'tabela2') {
        // Contar apenas Tabela 2
        statusParaContar = statusFinal2;
      } else {
        // "Todas" - Usar o STATUS GERAL DO PEDIDO baseado na pontuação combinada
        statusParaContar = calcularStatusGeralPedido(p);
      }

      // Contar o status determinado
      if (statusParaContar) {
        switch (statusParaContar) {
          case 'EXCELENTE': counts.excelentes++; break;
          case 'BOA VENDA': counts.boaVenda++; break;
          case 'VIÁVEL': counts.viaveis++; break;
          case 'EM ANÁLISE': counts.emAnalise++; break;
          case 'INVIÁVEL': counts.inviaveis++; break;
        }
      }
    });

    return counts;
  };

  const statusCounts = contarStatusTabelas();

  // Contadores de fase do fluxo
  // Cancelados agrupa TODOS os tipos de cancelamento para coincidir com a tela de Consulta de Pedidos
  const FASES_CANCELAMENTO = ['ORCAMENTO_CANCELADO', 'ORCAMENTO_RECUSADO', 'PEDIDO_CANCELADO', 'PEDIDO_CANCELADO_POS_FATURAMENTO', 'CANCELADO'];
  const faseCounts = {
    faturados: pedidosFiltrados.filter(p => p.fase_atual === 'PEDIDO_FATURADO').length,
    confirmados: pedidosFiltrados.filter(p => p.fase_atual === 'PEDIDO_CONFIRMADO').length,
    aguardando: pedidosFiltrados.filter(p => p.fase_atual === 'AGUARDANDO_PROXIMA_ETAPA').length,
    cancelados: pedidosFiltrados.filter(p => FASES_CANCELAMENTO.includes(p.fase_atual)).length,
    canceladosPosFaturamento: pedidosFiltrados.filter(p => p.fase_atual === 'PEDIDO_CANCELADO_POS_FATURAMENTO').length,
    arquivados: pedidosFiltrados.filter(p => p.fase_atual === 'ARQUIVADO').length
  };
  
  // Calcular totais considerando o filtro de tabela
  const calcularTotais = () => {
    let valor = 0;
    let custo = 0;
    
    pedidosFiltrados.forEach(p => {
      // Sempre adiciona valor de produtos especiais
      const valorProdutosEspeciais = parseFloat(p.valor_produtos_especiais) || 0;
      
      if (filtros.tabela === 'tabela1') {
        valor += (parseFloat(p.valor_tabela1) || 0) + valorProdutosEspeciais;
        custo += (parseFloat(p.custo_tabela1) || 0);
      } else if (filtros.tabela === 'tabela2') {
        valor += (parseFloat(p.valor_tabela2) || 0) + valorProdutosEspeciais;
        custo += (parseFloat(p.custo_tabela2) || 0);
      } else {
        valor += (parseFloat(p.valor_tabela1) || 0) + (parseFloat(p.valor_tabela2) || 0) + valorProdutosEspeciais;
        custo += (parseFloat(p.custo_tabela1) || 0) + (parseFloat(p.custo_tabela2) || 0);
      }
    });
    
    return { valor, custo };
  };
  
  const totaisCalculados = calcularTotais();
  const valorTotalFiltrado = totaisCalculados.valor;
  const custoTotalFiltrado = totaisCalculados.custo;
  const lucroTotalFiltrado = valorTotalFiltrado - custoTotalFiltrado;
  const margemMediaFiltrada = valorTotalFiltrado > 0 ? (lucroTotalFiltrado / valorTotalFiltrado) * 100 : 0;
  const ticketMedioFiltrado = totalPedidos > 0 ? valorTotalFiltrado / totalPedidos : 0;

  // Calcular preço médio por m² baseado nos pedidos filtrados
  // Usa os valores de forros da tabela pedido_itens_forro (não os valores totais do pedido)
  const calcularPrecoMedioM2Filtrado = () => {
    let totalM2 = 0;
    let totalValorForros = 0;
    let totalVendasGeral = 0;
    
    pedidosFiltrados.forEach(p => {
      // Calcular valor total do pedido (inclui forro, acessórios e produtos especiais)
      const valorProdutosEspeciais = parseFloat(p.valor_produtos_especiais) || 0;
      
      if (filtros.tabela === 'tabela1') {
        // Apenas Tabela 1
        totalM2 += parseFloat(p.total_m2_tab1) || 0;
        totalValorForros += parseFloat(p.valor_forro_tab1) || 0;
        totalVendasGeral += (parseFloat(p.valor_tabela1) || 0) + valorProdutosEspeciais;
      } else if (filtros.tabela === 'tabela2') {
        // Apenas Tabela 2
        totalM2 += parseFloat(p.total_m2_tab2) || 0;
        totalValorForros += parseFloat(p.valor_forro_tab2) || 0;
        totalVendasGeral += (parseFloat(p.valor_tabela2) || 0) + valorProdutosEspeciais;
      } else {
        // Todas as tabelas - usar campos gerais que somam tudo (sem filtro de tipo_tabela)
        totalM2 += parseFloat(p.total_m2_geral) || 0;
        totalValorForros += parseFloat(p.valor_forro_geral) || 0;
        totalVendasGeral += (parseFloat(p.valor_tabela1) || 0) + (parseFloat(p.valor_tabela2) || 0) + valorProdutosEspeciais;
      }
    });
    
    const precoMedio = totalM2 > 0 ? totalVendasGeral / totalM2 : 0;
    
    return {
      totalM2,
      totalVendas: totalVendasGeral,
      precoMedioM2: precoMedio
    };
  };
  
  const precoMedioCalculado = calcularPrecoMedioM2Filtrado();

  // Calcular valor total dos pedidos faturados
  const valorTotalFaturados = pedidosFiltrados
    .filter(p => p.fase_atual === 'PEDIDO_FATURADO')
    .reduce((sum, p) => sum + (parseFloat(p.valor_tabela1) || 0) + (parseFloat(p.valor_tabela2) || 0) + (parseFloat(p.valor_produtos_especiais) || 0), 0);

  // Calcular valor total dos cancelados (inclui TODOS os tipos: orçamento cancelado/recusado,
  // pedido cancelado, cancelado após faturamento e fase "CANCELADO" legado)
  const valorTotalCancelados = pedidosFiltrados
    .filter(p => FASES_CANCELAMENTO.includes(p.fase_atual))
    .reduce((sum, p) => sum + (parseFloat(p.valor_tabela1) || 0) + (parseFloat(p.valor_tabela2) || 0) + (parseFloat(p.valor_produtos_especiais) || 0), 0);
  
  // Valor em aberto = Valor Total - Valor Faturados - Valor Cancelados
  const valorEmAberto = valorTotalFiltrado - valorTotalFaturados - valorTotalCancelados;
  
  // Taxa de aprovação baseada na contagem de status (Excelente, Boa Venda, Viável são considerados aprovados)
  const totalStatusContados = statusCounts.excelentes + statusCounts.boaVenda + statusCounts.viaveis + 
                              statusCounts.emAnalise + statusCounts.inviaveis;
  const statusAprovadosTotal = statusCounts.excelentes + statusCounts.boaVenda + statusCounts.viaveis;
  const taxaAprovacaoCalculada = totalStatusContados > 0 ? (statusAprovadosTotal / totalStatusContados) * 100 : 0;

  // ==================== DADOS PARA GRÁFICOS ====================

  const pedidosPorDia = () => {
    const hoje = new Date();
    const dias = [];
    for (let i = 29; i >= 0; i--) {
      const data = new Date(hoje);
      data.setDate(data.getDate() - i);
      const dataStr = data.toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
      const pedidosDia = pedidosFiltrados.filter(p => {
        const dataPedido = new Date(p.created_at).toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
        return dataPedido === dataStr;
      });
      
      // Considerar filtro de tabela
      let valorDia = 0;
      pedidosDia.forEach(p => {
        const valorProdutosEspeciais = parseFloat(p.valor_produtos_especiais) || 0;
        if (filtros.tabela === 'tabela1') {
          valorDia += (parseFloat(p.valor_tabela1) || 0) + valorProdutosEspeciais;
        } else if (filtros.tabela === 'tabela2') {
          valorDia += (parseFloat(p.valor_tabela2) || 0) + valorProdutosEspeciais;
        } else {
          valorDia += (parseFloat(p.valor_tabela1) || 0) + (parseFloat(p.valor_tabela2) || 0) + valorProdutosEspeciais;
        }
      });
      
      dias.push({
        data: data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
        pedidos: pedidosDia.length,
        valor: Math.round(valorDia)
      });
    }
    return dias;
  };

  const pedidosPorStatus = () => {
    return [
      { name: 'Excelente', value: statusCounts.excelentes, color: CORES.azulEscuro },
      { name: 'Boa Venda', value: statusCounts.boaVenda, color: CORES.azulMedio },
      { name: 'Viável', value: statusCounts.viaveis, color: CORES.ciano },
      { name: 'Em Análise', value: statusCounts.emAnalise, color: CORES.amarelo },
      { name: 'Inviável', value: statusCounts.inviaveis, color: CORES.laranja }
    ].filter(s => s.value > 0);
  };

  const vendasPorVendedor = () => {
    const vendedores = {};
    pedidosFiltrados.forEach(p => {
      const vendedor = p.vendedor || 'Não informado';
      if (!vendedores[vendedor]) {
        vendedores[vendedor] = { nome: vendedor, pedidos: 0, valor: 0, custo: 0 };
      }
      vendedores[vendedor].pedidos += 1;
      
      // Considerar filtro de tabela
      if (filtros.tabela === 'tabela1') {
        const valorProdutosEspeciais = parseFloat(p.valor_produtos_especiais) || 0;
        vendedores[vendedor].valor += (parseFloat(p.valor_tabela1) || 0) + valorProdutosEspeciais;
        vendedores[vendedor].custo += (parseFloat(p.custo_tabela1) || 0);
      } else if (filtros.tabela === 'tabela2') {
        const valorProdutosEspeciais = parseFloat(p.valor_produtos_especiais) || 0;
        vendedores[vendedor].valor += (parseFloat(p.valor_tabela2) || 0) + valorProdutosEspeciais;
        vendedores[vendedor].custo += (parseFloat(p.custo_tabela2) || 0);
      } else {
        const valorProdutosEspeciais = parseFloat(p.valor_produtos_especiais) || 0;
        vendedores[vendedor].valor += (parseFloat(p.valor_tabela1) || 0) + (parseFloat(p.valor_tabela2) || 0) + valorProdutosEspeciais;
        vendedores[vendedor].custo += (parseFloat(p.custo_tabela1) || 0) + (parseFloat(p.custo_tabela2) || 0);
      }
    });
    return Object.values(vendedores)
      .map(v => ({
        ...v,
        valor: Math.round(v.valor),
        custo: Math.round(v.custo),
        lucro: Math.round(v.valor - v.custo),
        margem: v.valor > 0 ? ((v.valor - v.custo) / v.valor) * 100 : 0
      }))
      .sort((a, b) => b.valor - a.valor);
  };

  const getDiasFromPeriodo = () => {
    switch (periodoFinanceiro) {
      case '7dias': return 7;
      case '30dias': return 30;
      case '60dias': return 60;
      case '90dias': return 90;
      case '6meses': return 180;
      case '1ano': return 365;
      case '2anos': return 730;
      default: return 180;
    }
  };

  const pedidosPorPeriodo = () => {
    const dias = getDiasFromPeriodo();
    const hoje = new Date();
    const dataInicio = new Date(hoje);
    dataInicio.setDate(dataInicio.getDate() - dias);
    const agruparPorMes = dias > 60;
    const grupos = {};
    
    pedidosFiltrados.forEach(p => {
      const dataPedido = new Date(p.created_at);
      if (dataPedido >= dataInicio) {
        let chave;
        if (agruparPorMes) {
          chave = `${dataPedido.getFullYear()}-${String(dataPedido.getMonth() + 1).padStart(2, '0')}`;
        } else {
          const inicioSemana = new Date(dataPedido);
          inicioSemana.setDate(inicioSemana.getDate() - inicioSemana.getDay());
          chave = inicioSemana.toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
        }
        if (!grupos[chave]) {
          grupos[chave] = { chave, receita: 0, custo: 0, lucro: 0 };
        }
        
        // Considerar filtro de tabela
        if (filtros.tabela === 'tabela1') {
          const valorProdutosEspeciais = parseFloat(p.valor_produtos_especiais) || 0;
          grupos[chave].receita += (parseFloat(p.valor_tabela1) || 0) + valorProdutosEspeciais;
          grupos[chave].custo += (parseFloat(p.custo_tabela1) || 0);
        } else if (filtros.tabela === 'tabela2') {
          const valorProdutosEspeciais = parseFloat(p.valor_produtos_especiais) || 0;
          grupos[chave].receita += (parseFloat(p.valor_tabela2) || 0) + valorProdutosEspeciais;
          grupos[chave].custo += (parseFloat(p.custo_tabela2) || 0);
        } else {
          const valorProdutosEspeciais = parseFloat(p.valor_produtos_especiais) || 0;
          grupos[chave].receita += (parseFloat(p.valor_tabela1) || 0) + (parseFloat(p.valor_tabela2) || 0) + valorProdutosEspeciais;
          grupos[chave].custo += (parseFloat(p.custo_tabela1) || 0) + (parseFloat(p.custo_tabela2) || 0);
        }
      }
    });
    
    return Object.values(grupos)
      .map(g => ({
        ...g,
        receita: Math.round(g.receita),
        custo: Math.round(g.custo),
        lucro: Math.round(g.receita - g.custo),
        label: agruparPorMes 
          ? new Date(g.chave + '-01').toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })
          : new Date(g.chave).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
      }))
      .sort((a, b) => a.chave.localeCompare(b.chave));
  };

  const topClientes = () => {
    const clientes = {};
    pedidosFiltrados.forEach(p => {
      const clienteNome = p.clientes?.nome || 'Cliente não informado';
      if (!clientes[clienteNome]) {
        clientes[clienteNome] = { nome: clienteNome, pedidos: 0, valor: 0 };
      }
      clientes[clienteNome].pedidos += 1;
      
      // Considerar filtro de tabela
      const valorProdutosEspeciais = parseFloat(p.valor_produtos_especiais) || 0;
      if (filtros.tabela === 'tabela1') {
        clientes[clienteNome].valor += (parseFloat(p.valor_tabela1) || 0) + valorProdutosEspeciais;
      } else if (filtros.tabela === 'tabela2') {
        clientes[clienteNome].valor += (parseFloat(p.valor_tabela2) || 0) + valorProdutosEspeciais;
      } else {
        clientes[clienteNome].valor += (parseFloat(p.valor_tabela1) || 0) + (parseFloat(p.valor_tabela2) || 0) + valorProdutosEspeciais;
      }
    });
    return Object.values(clientes)
      .map(c => ({ ...c, valor: Math.round(c.valor) }))
      .sort((a, b) => b.valor - a.valor)
      .slice(0, 5);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-xl shadow-xl border border-gray-100">
          <p className="font-semibold text-gray-800 mb-1 text-sm">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-xs">
              {entry.name}: {entry.name.toLowerCase().includes('valor') || 
                            entry.name.toLowerCase().includes('receita') || 
                            entry.name.toLowerCase().includes('lucro') || 
                            entry.name.toLowerCase().includes('custo')
                ? formatarMoeda(entry.value) 
                : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Custom label para o gráfico de pizza - posicionado externamente
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 30;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="#666"
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize="11"
        fontWeight="500"
      >
        {`${name} ${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const botoesPeriodo = [
    { id: '7dias', label: '7D' },
    { id: '30dias', label: '30D' },
    { id: '60dias', label: '60D' },
    { id: '90dias', label: '90D' },
    { id: '6meses', label: '6M' },
    { id: '1ano', label: '1A' },
    { id: '2anos', label: '2A' },
  ];

  // ==================== COMPONENTE GLASS CARD ====================
  const GlassStatusCard = ({ label, value, color, icon: Icon, tooltip }) => (
    <div className="relative group" style={{ marginBottom: '8px' }}>
      {/* Card de fundo colorido - efeito líquido */}
      <div 
        className="absolute rounded-2xl w-full h-full"
        style={{
          background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`,
          top: '8px',
          left: '-8px',
          zIndex: 0,
          boxShadow: `0 4px 15px ${color}40`
        }}
      />
      
      {/* Card glass por cima */}
      <div 
        className="relative rounded-2xl p-4 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 cursor-default"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.9) 100%)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.9)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.8)',
          zIndex: 1
        }}
      >
        {/* Brilho no topo */}
        <div 
          className="absolute top-0 left-0 right-0 h-1/2 rounded-t-2xl pointer-events-none"
          style={{
            background: 'linear-gradient(180deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0) 100%)'
          }}
        />
        
        <div className="relative z-10">
          <div className="flex items-center gap-1.5 mb-2">
            <Icon size={14} style={{ color: color }} />
            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
          </div>
          <div className="text-2xl font-bold" style={{ color: color }}>{value}</div>
        </div>

        {/* Tooltip ao passar o mouse */}
        {tooltip && tooltip.length > 0 && (
          <div 
            className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 rounded-xl invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none whitespace-nowrap shadow-xl"
            style={{
              background: `linear-gradient(135deg, ${color}ee 0%, ${color}dd 100%)`,
              border: `1px solid ${color}`,
              zIndex: 50
            }}
          >
            <div className="font-semibold mb-1 text-white text-xs">Inclui:</div>
            {tooltip.map((item, index) => (
              <div key={index} className="text-white text-xs">• {item}</div>
            ))}
            <div 
              className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent"
              style={{ borderTopColor: `${color}dd` }}
            />
          </div>
        )}
      </div>
    </div>
  );

  // ==================== RENDERIZAÇÃO ====================

  if (loading) {
    return <Loading texto="Carregando..." />;
  }

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#f3f4f6' }}>
      <div className="max-w-7xl mx-auto">
        
        {/* Cabeçalho - Gradiente cinza elegante animado */}
        <div 
          className="rounded-2xl p-6 mb-6 text-white shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
          style={{ 
            background: 'linear-gradient(135deg, #9C9B9B 0%, #7a7a7a 25%, #9C9B9B 50%, #858585 75%, #9C9B9B 100%)',
            backgroundSize: '400% 400%',
            animation: 'gradientShift 8s ease infinite'
          }}
        >
          <div className="flex items-center justify-between mb-5">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <BarChart3 size={28} />
                Dashboard
              </h1>
              <p className="text-white/80 text-sm mt-1">
                {podeVerTodos() 
                  ? 'Visão geral de todos os pedidos do sistema' 
                  : `Visualizando: ${getNomeUsuario()}`}
              </p>
            </div>
            <button 
              onClick={carregarPedidos}
              className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl flex items-center gap-2 transition-all text-sm font-medium border border-white/30"
            >
              <RefreshCw size={16} />
              Atualizar
            </button>
          </div>

          {/* Cards Resumo */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/25 hover:scale-105 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-default group">
              <div className="flex items-center gap-2 mb-1">
                <ShoppingCart size={16} className="text-white/70 group-hover:rotate-12 transition-transform duration-300" />
                <span className="text-xs text-white/70">Total Pedidos</span>
              </div>
              <div className="text-2xl font-bold">{totalPedidos}</div>
            </div>
            <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/25 hover:scale-105 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-default group">
              <div className="flex items-center gap-2 mb-1">
                <PackageCheck size={16} className="text-white/70 group-hover:rotate-12 transition-transform duration-300" />
                <span className="text-xs text-white/70">Faturados</span>
              </div>
              <div className="text-2xl font-bold">{faseCounts.faturados}</div>
            </div>
            <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/25 hover:scale-105 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-default group">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign size={16} className="text-white/70 group-hover:scale-110 transition-transform duration-300" />
                <span className="text-xs text-white/70">Valor Total</span>
              </div>
              <div className="text-lg font-bold">{formatarMoeda(valorTotalFiltrado)}</div>
            </div>
            <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/25 hover:scale-105 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-default group">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle size={16} className="text-white/70 group-hover:rotate-12 transition-transform duration-300" />
                <span className="text-xs text-white/70">Valor Faturados</span>
              </div>
              <div className="text-lg font-bold">{formatarMoeda(valorTotalFaturados)}</div>
            </div>
            <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/25 hover:scale-105 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-default group">
              <div className="flex items-center gap-2 mb-1">
                <Clock size={16} className="text-white/70 group-hover:rotate-12 transition-transform duration-300" />
                <span className="text-xs text-white/70">Valor em Análise</span>
              </div>
              <div className="text-lg font-bold">{formatarMoeda(valorEmAberto)}</div>
            </div>
            <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/25 hover:scale-105 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-default group">
              <div className="flex items-center gap-2 mb-1">
                <XCircle size={16} className="text-white/70 group-hover:rotate-12 transition-transform duration-300" />
                <span className="text-xs text-white/70">Valor Cancelados</span>
              </div>
              <div className="text-lg font-bold">{formatarMoeda(valorTotalCancelados)}</div>
            </div>
          </div>
        </div>

        {/* Linha divisória */}
        <div className="w-full h-px bg-gray-200 my-6"></div>

        {/* Filtros */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6 hover:shadow-xl hover:scale-[1.01] hover:-translate-y-1 transition-all duration-300">
          <div className="flex gap-3 mb-4">
            <div className="flex-1 flex items-center gap-2">
              <Filter size={20} className="text-gray-400" />
              <span className="font-semibold text-gray-700">Filtros</span>
            </div>
            <button
              onClick={() => setMostrarFiltros(!mostrarFiltros)}
              className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 hover:scale-105 hover:shadow-lg flex items-center gap-2 font-semibold transition-all duration-300 shadow-md group"
            >
              {mostrarFiltros ? <ChevronUp size={18} className="group-hover:-translate-y-0.5 transition-transform duration-300" /> : <ChevronDown size={18} className="group-hover:translate-y-0.5 transition-transform duration-300" />}
              {mostrarFiltros ? 'Ocultar' : 'Mostrar'}
            </button>
          </div>

          {mostrarFiltros && (
            <div className="border-t-2 border-gray-100 pt-4">
              {/* Primeira linha de filtros */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Data Pedido Início</label>
                  <input
                    type="date"
                    value={filtros.dataInicio}
                    onChange={(e) => setFiltros({ ...filtros, dataInicio: e.target.value })}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 cursor-pointer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Data Pedido Fim</label>
                  <input
                    type="date"
                    value={filtros.dataFim}
                    onChange={(e) => setFiltros({ ...filtros, dataFim: e.target.value })}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 cursor-pointer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Data Faturamento Início</label>
                  <input
                    type="date"
                    value={filtros.dataFaturamentoInicio}
                    onChange={(e) => setFiltros({ ...filtros, dataFaturamentoInicio: e.target.value })}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 cursor-pointer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Data Faturamento Fim</label>
                  <input
                    type="date"
                    value={filtros.dataFaturamentoFim}
                    onChange={(e) => setFiltros({ ...filtros, dataFaturamentoFim: e.target.value })}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 cursor-pointer"
                  />
                </div>
                {podeVerTodos() && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Responsável</label>
                    <select
                      value={filtros.responsavel}
                      onChange={(e) => setFiltros({ ...filtros, responsavel: e.target.value })}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 cursor-pointer"
                    >
                      <option value="">Todos</option>
                      {listaResponsaveis.map((resp, idx) => (
                        <option key={idx} value={resp}>{resp}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Tipo</label>
                  <select
                    value={filtros.tipo}
                    onChange={(e) => setFiltros({ ...filtros, tipo: e.target.value })}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 cursor-pointer"
                  >
                    <option value="">Todos</option>
                    <option value="ORCAMENTO">Orçamento</option>
                    <option value="PEDIDO">Pedido</option>
                  </select>
                </div>
              </div>
              {/* Segunda linha de filtros */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Status do Fluxo</label>
                  <select
                    value={filtros.fase}
                    onChange={(e) => setFiltros({ ...filtros, fase: e.target.value })}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 cursor-pointer"
                  >
                    <option value="">Todos os status</option>
                    <option value="AGUARDANDO_PROXIMA_ETAPA">Aguardando Próxima Etapa</option>
                    <option value="PEDIDO_CONFIRMADO">Pedido Confirmado</option>
                    <option value="PEDIDO_FATURADO">Pedido Faturado</option>
                    <option value="CANCELADOS_TODOS">Cancelado</option>
                    <option value="PEDIDO_CANCELADO_POS_FATURAMENTO">  → Apenas Cancelado Após Faturamento</option>
                    <option value="ARQUIVADO">Arquivado</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Classificação do Pedido</label>
                  <select
                    value={filtros.classificacao}
                    onChange={(e) => setFiltros({ ...filtros, classificacao: e.target.value })}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 cursor-pointer"
                  >
                    <option value="">Todos</option>
                    <option value="EXCELENTE">Excelente</option>
                    <option value="BOA VENDA">Boa Venda</option>
                    <option value="VIÁVEL">Viável</option>
                    <option value="EM ANÁLISE">Em Análise</option>
                    <option value="INVIÁVEL">Inviável</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Rede</label>
                  <select
                    value={filtros.rede}
                    onChange={(e) => setFiltros({ ...filtros, rede: e.target.value })}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 cursor-pointer"
                  >
                    <option value="">Todas</option>
                    {listaRedes.map(rede => (
                      <option key={rede} value={rede}>{rede}</option>
                    ))}
                  </select>
                </div>
                {['admin', 'diretoria', 'administrador', 'administrativo', 'vendas'].includes((usuarioLogado?.departamento || '').toLowerCase().trim()) && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Produtos Especiais</label>
                    <select
                      value={filtros.produtosEspeciais}
                      onChange={(e) => setFiltros({ ...filtros, produtosEspeciais: e.target.value })}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl hover:border-blue-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 cursor-pointer"
                    >
                      <option value="">Todos</option>
                      <option value="sim">Com Produtos Especiais</option>
                      <option value="nao">Sem Produtos Especiais</option>
                    </select>
                  </div>
                )}
                <div className="flex items-end">
                  <button
                    onClick={() => setFiltros({ ...filtros, dataInicio: '', dataFim: '', dataFaturamentoInicio: '', dataFaturamentoFim: '', responsavel: '', tipo: '', fase: '', classificacao: '', rede: '', produtosEspeciais: '' })}
                    className="w-full p-3 rounded-xl font-semibold text-white bg-gray-500 hover:bg-gray-600 hover:scale-105 hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 group"
                  >
                    <X size={18} className="group-hover:rotate-90 transition-transform duration-300" />
                    Limpar
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Linha divisória */}
        <div className="w-full h-px bg-gray-200 my-6"></div>

        {/* ==================== CARDS DE FASE DO FLUXO ==================== */}
        <div className="mb-4 px-2">
          <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
            <Activity size={20} className="text-blue-600" />
            Status do Fluxo
          </h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8 px-2">
          <GlassStatusCard 
            label="Faturados" 
            value={faseCounts.faturados} 
            color="#3B82F6" 
            icon={PackageCheck} 
          />
          <GlassStatusCard 
            label="Confirmados" 
            value={faseCounts.confirmados} 
            color="#10B981" 
            icon={CheckCircle} 
          />
          <GlassStatusCard 
            label="Aguardando" 
            value={faseCounts.aguardando} 
            color="#F59E0B" 
            icon={Clock} 
          />
          <GlassStatusCard 
            label="Cancelados" 
            value={faseCounts.cancelados} 
            color="#EF4444" 
            icon={XCircle} 
          />
          <GlassStatusCard 
            label="Arquivados" 
            value={faseCounts.arquivados} 
            color="#6B7280" 
            icon={Archive} 
          />
        </div>

        {/* Linha divisória */}
        <div className="w-full h-px bg-gray-200 my-6"></div>

        {/* ==================== CARDS DE STATUS - GLASS STYLE ==================== */}
        <div className="mb-4 px-2">
          <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
            <Award size={20} className="text-blue-600" />
            Classificação do Pedido
          </h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8 px-2">
          <GlassStatusCard label="Excelente" value={statusCounts.excelentes} color={CORES.azulEscuro} icon={Award} />
          <GlassStatusCard label="Boa Venda" value={statusCounts.boaVenda} color={CORES.azulMedio} icon={CheckCircle} />
          <GlassStatusCard label="Viável" value={statusCounts.viaveis} color={CORES.ciano} icon={TrendingUp} />
          <GlassStatusCard label="Em Análise" value={statusCounts.emAnalise} color={CORES.amarelo} icon={Clock} />
          <GlassStatusCard label="Inviável" value={statusCounts.inviaveis} color={CORES.laranja} icon={AlertTriangle} />
        </div>

        {/* Linha divisória */}
        <div className="w-full h-px bg-gray-200 my-6"></div>

        {/* Card de Preço Médio por m² + Gráfico unificado */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {/* Card de Preço Médio por m² */}
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Package size={16} style={{ color: CORES.laranja }} />
              Preço Médio
            </h3>
            <div className="flex flex-col items-center justify-center h-[200px]">
              <div className="text-5xl font-bold mb-2" style={{ color: CORES.laranja }}>
                {formatarMoeda(precoMedioCalculado.precoMedioM2)}
              </div>
              <p className="text-gray-500 text-sm text-center mb-4">
                Preço médio por m² de forro
              </p>
              <div className="grid grid-cols-2 gap-4 w-full mt-2">
                <div className="text-center p-3 bg-gray-50 rounded-xl">
                  <p className="text-xs text-gray-500">Total m² (Forro)</p>
                  <p className="text-lg font-bold text-gray-700">
                    {precoMedioCalculado.totalM2.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-xl">
                  <p className="text-xs text-gray-500">Total Vendas</p>
                  <p className="text-lg font-bold text-gray-700">
                    {formatarMoeda(precoMedioCalculado.totalVendas)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Gráfico Unificado - Pedidos e Valor por Dia */}
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 lg:col-span-2">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Activity size={16} style={{ color: CORES.azulMedio }} />
              {filtros.tipo === 'ORCAMENTO' ? 'Orçamentos' : filtros.tipo === 'PEDIDO' ? 'Pedidos' : 'Vendas'} e Valor por Dia
              <span className="ml-auto flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: CORES.azulMedio }}></span>
                  {filtros.tipo === 'ORCAMENTO' ? 'Orçamentos' : filtros.tipo === 'PEDIDO' ? 'Pedidos' : 'Vendas'}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: CORES.ciano }}></span>
                  Valor
                </span>
              </span>
            </h3>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={pedidosPorDia()}>
                <defs>
                  <linearGradient id="colorPedidos" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CORES.azulMedio} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={CORES.azulMedio} stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorValor" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CORES.ciano} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={CORES.ciano} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="data" tick={{ fontSize: 10, fill: '#999' }} interval={5} />
                <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#999' }} orientation="left" />
                <YAxis yAxisId="right" tick={{ fontSize: 10, fill: '#999' }} orientation="right" tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Area yAxisId="left" type="monotone" dataKey="pedidos" stroke={CORES.azulMedio} strokeWidth={2} fill="url(#colorPedidos)" name={filtros.tipo === 'ORCAMENTO' ? 'Orçamentos' : filtros.tipo === 'PEDIDO' ? 'Pedidos' : 'Vendas'} />
                <Area yAxisId="right" type="monotone" dataKey="valor" stroke={CORES.ciano} strokeWidth={2} fill="url(#colorValor)" name="Valor" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Linha divisória */}
        <div className="w-full h-px bg-gray-200 my-6"></div>

        {/* Pizza + Top Clientes */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <PieChartIcon size={16} style={{ color: CORES.azulEscuro }} />
              Distribuição por Status
            </h3>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={pedidosPorStatus()}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={65}
                  paddingAngle={2}
                  dataKey="value"
                  label={renderCustomizedLabel}
                  labelLine={{ stroke: '#ccc', strokeWidth: 1 }}
                >
                  {pedidosPorStatus().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 lg:col-span-2 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Users size={16} style={{ color: CORES.azulMedio }} />
              Top 5 Clientes
            </h3>
            <div className="space-y-2">
              {topClientes().map((cliente, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                  <div className="flex items-center gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm"
                      style={{ backgroundColor: index === 0 ? CORES.amarelo : index === 1 ? CORES.cinza : index === 2 ? '#D97706' : '#E5E7EB' }}
                    >
                      {index + 1}
                    </span>
                    <span className="text-sm text-gray-700 font-medium">{cliente.nome}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-xs px-2 py-0.5 rounded-full text-white shadow-sm" style={{ backgroundColor: CORES.azulMedio }}>
                      {cliente.pedidos} pedidos
                    </span>
                    <span className="text-sm font-bold" style={{ color: CORES.azulEscuro }}>{formatarMoeda(cliente.valor)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Linha divisória */}
        <div className="w-full h-px bg-gray-200 my-6"></div>

        {/* ==================== ÁREA RESTRITA ==================== */}
        {podeVerVisaoAdmin() && (
          <>
            {/* Visão Financeira */}
            <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm border border-gray-100 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
              <div className="flex items-center gap-2 mb-5">
                <Eye size={18} style={{ color: CORES.azulMedio }} />
                <h2 className="text-lg font-bold text-gray-800">Visão Financeira</h2>
                <span className="text-xs text-gray-400 ml-2">Diretoria / Administração</span>
                {filtros.tabela !== 'todas' && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-lg font-medium ml-2">
                    {filtros.tabela === 'tabela1' ? 'Tabela 1' : 'Tabela 2'}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 hover:shadow-lg hover:scale-105 hover:-translate-y-1 transition-all duration-300 cursor-default">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign size={16} style={{ color: CORES.azulMedio }} />
                    <span className="text-xs text-gray-500">Receita Total</span>
                  </div>
                  <div className="text-xl font-bold text-gray-800">{formatarMoeda(valorTotalFiltrado)}</div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 hover:shadow-lg hover:scale-105 hover:-translate-y-1 transition-all duration-300 cursor-default">
                  <div className="flex items-center gap-2 mb-2">
                    <ArrowDown size={16} style={{ color: CORES.vermelho }} />
                    <span className="text-xs text-gray-500">Custo Total</span>
                  </div>
                  <div className="text-xl font-bold" style={{ color: CORES.vermelho }}>{formatarMoeda(custoTotalFiltrado)}</div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 hover:shadow-lg hover:scale-105 hover:-translate-y-1 transition-all duration-300 cursor-default">
                  <div className="flex items-center gap-2 mb-2">
                    <ArrowUp size={16} style={{ color: CORES.verde }} />
                    <span className="text-xs text-gray-500">Lucro Total</span>
                  </div>
                  <div className="text-xl font-bold" style={{ color: CORES.verde }}>{formatarMoeda(lucroTotalFiltrado)}</div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 hover:shadow-lg hover:scale-105 hover:-translate-y-1 transition-all duration-300 cursor-default">
                  <div className="flex items-center gap-2 mb-2">
                    <Percent size={16} style={{ color: CORES.verde }} />
                    <span className="text-xs text-gray-500">Margem Média</span>
                  </div>
                  <div className="text-xl font-bold" style={{ color: CORES.verde }}>
                    {formatarPorcentagem(margemMediaFiltrada)}
                  </div>
                </div>
              </div>
            </div>

            {/* Linha divisória */}
            <div className="w-full h-px bg-gray-200 my-6"></div>

            {/* Gráfico Receita vs Custo vs Lucro */}
            <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm border border-gray-100 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Zap size={16} style={{ color: CORES.amarelo }} />
                  Receita vs Custo vs Lucro
                </h3>
                <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
                  {botoesPeriodo.map((btn) => (
                    <button
                      key={btn.id}
                      onClick={() => setPeriodoFinanceiro(btn.id)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                        periodoFinanceiro === btn.id
                          ? 'bg-white text-gray-800 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={pedidosPorPeriodo()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#999' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#999' }} tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Bar dataKey="receita" fill={CORES.azulMedio} name="Receita" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="custo" fill={CORES.vermelho} name="Custo" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="lucro" fill={CORES.verde} name="Lucro" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}

        {/* Linha divisória */}
        <div className="w-full h-px bg-gray-200 my-6"></div>

        {podeVerDashboardsAnaliticos() && (
          <>
            {/* DASHBOARD ANALÍTICO DE VENDEDORES */}
            <DashboardAnalitico 
              pedidosFiltrados={pedidosFiltrados} 
              listaResponsaveis={listaResponsaveis}
              formatarMoeda={formatarMoeda}
              formatarPorcentagem={formatarPorcentagem}
              CORES={CORES}
            />

            {/* Linha divisória */}
            <div className="w-full h-px bg-gray-200 my-6"></div>

            {/* DASHBOARD ANALÍTICO DE PRODUTOS */}
            <DashboardAnaliticoProdutos 
              pedidosFiltrados={pedidosFiltrados}
              formatarMoeda={formatarMoeda}
              formatarPorcentagem={formatarPorcentagem}
              CORES={CORES}
            />
          </>
        )}

        {/* DASHBOARD DE CLIENTES - Visível para todos (o filtro por usuário é feito internamente) */}
        <>
          {/* Linha divisória */}
          <div className="w-full h-px bg-gray-200 my-6"></div>

          <DashboardClientes 
            formatarMoeda={formatarMoeda}
            CORES={CORES}
          />
        </>

        {/* Rodapé */}
        <div className="text-center text-xs text-gray-400 py-6">
          Atualizado em {new Date().toLocaleString('pt-BR')}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;